import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useFieldTheme } from 'framework/field/hooks/useFieldTheme';

import { useDropdownContext } from '../../dropdown/context/DropdownContext';
import { useOptionsTheme } from '../../Options/hooks/useOptionsTheme';
import { usePositionPlacement } from '../../Options/hooks/usePositionPlacement';
import { OptionsProps } from '../../Options/types';
import { useLoadingBlur } from '../hooks/useLoadingBlur';

import cn from '../Options.module.scss';

export const Options = ({ height = 210, children }: OptionsProps) => {
  const { fieldRef, theme, optionsRef, options, extend, is_open, loading } = useDropdownContext();

  const [is_visible, setVisible] = useState(false);

  useEffect(() => {
    let timer: number;
    if (is_open) {
      timer = requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }

    return () => {
      if (timer) cancelAnimationFrame(timer);
    };
  }, [is_open]);

  const { blur } = useLoadingBlur({ loading });

  const { border, background } = useFieldTheme(theme?.field);
  const { general, settings } = useOptionsTheme(theme?.options);

  const options_padding = settings.padding * 2;
  const options_border = 2;

  const total_height =
    options.length * settings.option_height +
    options_padding +
    settings.option_offset +
    options_border;

  const min_height =
    settings.option_height + options_padding + settings.option_offset + options_border;

  const container_height = Math.max(
    min_height,
    Math.min(total_height, height + settings.option_offset)
  );

  const position = usePositionPlacement({
    fieldRef,
    optionsRef,
    container_height,
    extend,
    is_open,
  });

  if (!is_open) return null;

  const content = (
    <div
      ref={optionsRef}
      className={classNames(cn.options, { [cn.visible]: is_visible })}
      onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
      style={{
        fontFamily: general.font_family,
        fontSize: general.font_size,
        padding: settings.padding,
        borderRadius: general.border_radius,
        background: background.normal,
        border: border.normal,
        maxHeight: height,
        ...position,
      }}
    >
      <div className={classNames({ [cn.blur]: blur })}>{children}</div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};
